import { differenceInDays, endOfDay } from 'date-fns';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { formValueSelector, getFormValues } from 'redux-form';
import { Action } from 'typescript-fsa';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import api from '../../../core/api';
import { getReportFormat } from '../../selected/selected.selector';
import { LoadersNames } from '../../ui/ui.constants';
import * as actions from './reports.actions';
import {
  getReport,
  getReportByPreset,
  getReportData,
  getReportFilterData,
  updateInventoryReportRow,
} from './reports.actions';
import { PresetParams, ReportFormat } from './reports.types';
import { composeErrorMessage, filterReportSettings } from '../../../core/helpers/utils/utils';
import { successHandler } from '../../../core/helpers/utils/sagaUtils';
import {
  labelToTypeMap,
  PresetDateRanges,
  reportPreset,
  reportSettings as defaultReportSettings,
  DateRangeType,
} from '../../../config/constants';
import { PresetSettings } from '../../../core/api/preset/preset.types';
import { Forms } from '../../form/form.selector';
import {
  getCheckedFeedingPeriods,
  getSelectedMixersScaleNumber,
} from '../../application/loadMixers/loadMixers.selector';
import { notificationError } from '../../../core/helpers/utils/notificationUtils';
import { codeSelector, reportsSelector, settingsSelector } from '../../selectors';
import { DownloadEntityType, InventoryReportType, reportDownloadFormats } from './reports.constants';
import { CodeTypeNames } from '../code/code.types';
import { getIngredientRequest } from '../ingredients/ingredients.actions';
import { toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';
import { getDaysBack, getRangeType } from '../../../shared/DateRangePicker/utils';

export function* getDownloadListSaga(action: Action<{ entity: string; fileName: string; formatId: string }>) {
  yield put(showLoader(LoadersNames.downloadListReport));
  const selectedFormat: ReportFormat = yield select(getReportFormat, action.payload.formatId);

  const params: any = {
    hideNotInUse: true,
  };

  const formNameByEntity = {
    ingredients: Forms.ingredientFilterForm,
    recipes: Forms.recipesFilterForm,
    premixes: Forms.premixesFilterForm,
    feedings: Forms.loadMixersForm,
    feedingsLoads: DownloadEntityType.FeedingsLoads,
    feedingsList: DownloadEntityType.FeedingsList,
    lots: Forms.lotsFilterForm,
  };

  if (formNameByEntity[action.payload.entity]) {
    const valuesSelector = formValueSelector(formNameByEntity[action.payload.entity]);
    params.hideNotInUse = yield select(valuesSelector, 'hideNotInUse');
    params.mixers = yield select(getSelectedMixersScaleNumber);
    params.periods = yield select(getCheckedFeedingPeriods);
    params.hideClosed = yield select(valuesSelector, 'hideClosed');
  }

  try {
    yield call(
      api.reports.downloadListReport,
      `application/${selectedFormat.type}`,
      selectedFormat.ext,
      action.payload.entity,
      action.payload.fileName,
      params,
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.downloadListReport));
}

export function* getReportSaga({ payload, meta }: ReturnType<typeof getReport.started>) {
  yield put(showLoader(LoadersNames.downloadReport));
  const daysBack = differenceInDays(payload.to, payload.from);
  const formSelector = yield call(getFormValues, Forms.reportsPageForm);
  const reportsPageForm = yield select(formSelector);
  const { viewSettings, dataSettings, ...settings } = reportsPageForm?.settings || defaultReportSettings[payload.type];
  const timezone = yield select(settingsSelector.getTimeZone);
  const endDate = payload.to;
  const preset: PresetSettings = {
    ...reportPreset[payload.type],
    dataSettings: {
      ...settings,
      ...dataSettings,
      daysBack: Object.values(labelToTypeMap).includes(reportsPageForm?.settings?.dateRangeType) ? undefined : daysBack,
      endDate: Object.values(labelToTypeMap).includes(reportsPageForm?.settings?.dateRangeType)
        ? undefined
        : toTimezoneDate(toLocalDate(endDate), timezone),
    },
    viewSettings,
  };

  const format = payload.format || reportDownloadFormats[0];
  try {
    yield call(
      api.reports.downloadReportByPreset,
      payload.type,
      `application/${format.type}`,
      format.ext,
      preset,
      payload.name || '',
    );
    successHandler(meta);
    yield put(
      actions.getReport.done({
        params: payload,
        result: {},
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.downloadReport));
}

export function* getReportByPresetSaga({ payload, meta }: ReturnType<typeof getReportByPreset.started>) {
  yield put(showLoader(LoadersNames.downloadReport));
  const selectedFormat = payload.format;

  const {
    dataSettings: { dateRangeType, daysBack, endDate },
  } = payload;

  const labelKey = dateRangeType as DateRangeType;

  try {
    yield call(
      api.reports.downloadReportByPreset,
      payload.reportType,
      `application/${selectedFormat!.type}`,
      selectedFormat!.ext,
      {
        dataSettings: {
          dateRangeType: dateRangeType,
          daysBack: labelKey === DateRangeType.LAST_MONTH ? undefined : daysBack,
          endDate:
            labelKey === DateRangeType.LAST_MONTH
              ? undefined
              : typeof endDate === 'string'
              ? new Date(endDate).toISOString()
              : toLocalDate(endDate || new Date()).toISOString(),
        },
        viewSettings: payload.viewSettings,
      },
      payload.name || '',
    );
    successHandler(meta);
    yield put(
      actions.getReportByPreset.done({
        params: payload,
        result: {},
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.downloadReport));
}

export function* getReportDataByPresetSaga(action: ReturnType<typeof actions.getReportDataByPreset.started>) {
  yield put(showLoader(LoadersNames.fetchReport));
  const { payload } = action;
  try {
    const filterDataDefault = yield select(reportsSelector.getDefaultReportsFilterData);
    const reportSettings = filterReportSettings(payload.preset.dataSettings, filterDataDefault);

    const preset = {
      viewSettings: payload.preset.viewSettings,
      dataSettings: reportSettings,
    };

    const result = yield call(api.reports.getReportDataByPreset, payload.type, preset);
    yield put(
      actions.getReportData.done({
        params: payload as any,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }

  yield put(hideLoader(LoadersNames.fetchReport));
}

const createPreset = (presetParams: PresetParams) => {
  const { payload, reportSettings, labelKey, daysBack, endDate, viewSettings } = presetParams;

  return {
    ...reportPreset[payload.type],
    dataSettings: {
      ...reportSettings,
      dateRangeType: getRangeType(labelKey),
      daysBack: getDaysBack(labelKey, daysBack),
      endDate:
        labelKey === PresetDateRanges.LAST_MONTH
          ? undefined
          : typeof endDate === 'string'
          ? new Date(endDate).toISOString()
          : toLocalDate(endDate).toISOString(),
      errorTolerance: reportSettings.errorTolerance ? +reportSettings.errorTolerance : undefined,
    },
    viewSettings,
  };
};

export function* getReportDataSaga({ payload, meta }: ReturnType<typeof getReportData.started>) {
  yield put(showLoader(LoadersNames.fetchReport));
  try {
    const daysBack = differenceInDays(payload.to, payload.from);
    const reportsPageFormSelector = yield call(getFormValues, Forms.reportsPageForm);
    const reportsSettingsFormSelector = yield call(getFormValues, Forms.reportSettingsForm);
    const reportsPageForm = yield select(reportsPageFormSelector);
    const reportSettingsForm = yield select(reportsSettingsFormSelector);
    const timezone = yield select(settingsSelector.getTimeZone);
    const filterDataDefault = yield select(reportsSelector.getDefaultReportsFilterData);
    const { viewSettings, ...settings } =
      (reportsPageForm && reportsPageForm.settings) || reportSettingsForm || defaultReportSettings[payload.type];
    const reportSettings = filterReportSettings(settings, filterDataDefault);
    const endDate = toTimezoneDate(endOfDay(payload.to), timezone);
    const labelKey = reportsPageForm?.selectedTimeRange?.labelKey;
    const presetParams = { viewSettings, payload, labelKey, daysBack, endDate, reportSettings };
    const preset = createPreset(presetParams);
    const result = yield call(api.reports.getReportDataByPreset, payload.type, {
      ...preset,
      dataSettings: {
        ...preset.dataSettings,
        showBatchNo: settings?.dataSettings?.showBatchNo,
        includedIngredients: payload.includedIngredients || preset.dataSettings.includedIngredients,
        includedRations: payload.includedRations || preset.dataSettings.includedRations,
        includedGroups: payload.includedGroups || preset.dataSettings.includedGroups,
        movementTypes: preset.dataSettings.movementTypes
          ? preset.dataSettings.movementTypes.map((type) => +type)
          : undefined,
      },
    });

    successHandler(meta);
    yield put(
      actions.getReportData.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchReport));
}

export function* getReportFilterDataSaga({ payload, meta }: ReturnType<typeof getReportFilterData.started>) {
  yield put(showLoader(LoadersNames.fetchReportFilterData));
  try {
    const result = yield call(
      api.reports.getReportFilterData,
      payload,
      payload !== InventoryReportType.Inventory,
      false,
    );

    successHandler(meta);
    yield put(
      actions.getReportFilterData.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchReportFilterData));
}

export function* updateInventoryReportRowSaga({ payload }: ReturnType<typeof updateInventoryReportRow.started>) {
  try {
    const result = yield call(api.ingredients.getIngredient, payload);
    const priceUnitCodes = yield select(codeSelector.getCodeListFilteredByType, {
      codeType: CodeTypeNames.priceUnitCodes,
      includeInactive: true,
    });
    const codeValue = priceUnitCodes[result.priceUnitCode].value1;
    yield put(
      updateInventoryReportRow.done({
        result: {
          ...result,
          avgDailyUsage: result.avgDailyUsage / codeValue,
          stock: result.stock / codeValue,
          stockMax: result.stockMax / codeValue,
          stockDry: result.stockDry / codeValue,
          totalPrice: result.price * (result.stock || 0),
        },
        params: payload,
      }),
    );
    yield put(
      getIngredientRequest.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* filterReportDataSaga(action: ReturnType<typeof actions.filterReportData.started>) {
  yield put(showLoader(LoadersNames.fetchReport));
  const { payload } = action;
  try {
    const formSelector = yield call(getFormValues, Forms.reportsPageForm);
    const reportsPageForm = yield select(formSelector);
    const timezone = yield select(settingsSelector.getTimeZone);
    const { startDate, labelKey } = reportsPageForm.selectedTimeRange;
    const endDate = toLocalDate(
      toTimezoneDate(endOfDay(reportsPageForm.selectedTimeRange.endDate), timezone),
    ).toISOString();
    const daysBack = differenceInDays(reportsPageForm.selectedTimeRange.endDate, startDate);
    const filterDataDefault = yield select(reportsSelector.getDefaultReportsFilterData);
    const { viewSettings } = (reportsPageForm && reportsPageForm.settings) || defaultReportSettings[payload.type];
    const reportSettings = filterReportSettings(payload.filterSettings as any, filterDataDefault);
    const presetParams = { viewSettings, payload, labelKey, daysBack, endDate, reportSettings };
    const preset = createPreset(presetParams);
    const result = yield call(api.reports.getReportDataByPreset, payload.type, preset);
    yield put(
      actions.getReportData.done({
        params: payload as any,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchReport));
}

export function* watchReportsSaga() {
  yield all([
    yield takeLatest(actions.getDownloadList.started, getDownloadListSaga),
    yield takeLatest(actions.getReport.started, getReportSaga),
    yield takeLatest(actions.getReportByPreset.started, getReportByPresetSaga),
    yield takeLatest(actions.getReportDataByPreset.started, getReportDataByPresetSaga),
    yield takeLatest(actions.getReportData.started, getReportDataSaga),
    yield takeLatest(actions.getReportFilterData.started, getReportFilterDataSaga),
    yield takeLatest(actions.updateInventoryReportRow.started, updateInventoryReportRowSaga),
    yield takeLatest(actions.filterReportData.started, filterReportDataSaga),
  ]);
}
